<template>
  <big-loader v-if="isLoading"/>
  <section v-else :key="globalKey">

    <TableToolbar :buttons="['info']" :info="dictionary.messages.sectors">
      <template #main>
        <div id="back" v-if="holdingsToDisplay" @click="back()">
          <i style="font-size: 22px"
             :class="uiDirection === 'ltr' ? 'ri-arrow-left-wide-line' : 'ri-arrow-right-s-line'"/>
          {{ holdingsToDisplay?.name }}
        </div>
        <div v-else/>
      </template>
    </TableToolbar>

    <DataGridTable :is-compact="true"
                   :key="'sectors'"
                   :data="sectors"
                   :hovered-columns="true"
                   :loaded="isLoading"
                   v-if="!holdingsToDisplay"
                   :height-decrease="67"
                   :default-sort="'1D'"
                   :heightElements="['calendarFilterPanel']"
                   :default-sort-direction="-1"
                   :fixed-col="0">
      <template #thead="{thClick, sortKey, sortDirection, toggleSymbolActions, showSymbolActions}">
        <HeadCell v-for="(th, thIndex) in sectorsHeaders"
                  :key="`th-${thIndex}`"
                  :allowActionsHiding="th.allowActionsHiding"
                  :header="th"
                  :sortKey="sortKey"
                  :sortDirection="sortDirection"
                  :sortValue="th.sortValue"
                  :showSymbolActions="showSymbolActions"
                  @toggleSymbolActions="toggleSymbolActions()"
                  @sort="thClick(th.sortValue, thIndex)">
          {{ th.label }}
        </HeadCell>
      </template>
      <template #tbody="{rows, showSymbolActions}">
        <tr v-for="(row, rowIndex) in rows" :key="`r-${rowIndex}`">
          <td style="cursor: pointer">
            <symbol-tag @symbolClicked="rowClick(row)" :tags="row.tags"
                        :options="{link: false, chart: true, logo: false, watchlist: false, chartSymbol: row.symbol}"
                        :showSymbolActions="showSymbolActions" :symbol="row.name"/>
          </td>

          <td class="bidi"
              :class="{bestPerformer: bestPerformance.performance[0] === row.symbol, worsePerformer: bestPerformance.worsePerformance[0] === row.symbol}">
            {{ formatChange(row['1D']) }}
          </td>
          <td class="bidi"
              :class="{bestPerformer: bestPerformance.performance[0] === row.symbol, worsePerformer: bestPerformance.worsePerformance[0] === row.symbol}">
            {{ formatChange(row['5D']) }}
          </td>
          <td class="bidi"
              :class="{bestPerformer: bestPerformance.performance[0] === row.symbol, worsePerformer: bestPerformance.worsePerformance[0] === row.symbol}">
            {{ formatChange(row['10D']) }}
          </td>
          <td class="bidi"
              :class="{bestPerformer: bestPerformance.performance[1] === row.symbol, worsePerformer: bestPerformance.worsePerformance[1] === row.symbol}">
            {{ formatChange(row['1M']) }}
          </td>
          <td class="bidi"
              :class="{bestPerformer: bestPerformance.performance[2] === row.symbol, worsePerformer: bestPerformance.worsePerformance[2] === row.symbol}">
            {{ formatChange(row['6M']) }}
          </td>
          <td class="bidi"
              :class="{bestPerformer: bestPerformance.performance[3] === row.symbol, worsePerformer: bestPerformance.worsePerformance[3] === row.symbol}">
            {{ formatChange(row['ytd']) }}
          </td>
          <td class="bidi"
              :class="{bestPerformer: bestPerformance.performance[4] === row.symbol, worsePerformer: bestPerformance.worsePerformance[4] === row.symbol}">
            {{ formatChange(row['3Y']) }}
          </td>
          <td class="bidi"
              :class="{bestPerformer: bestPerformance.performance[5] === row.symbol, worsePerformer: bestPerformance.worsePerformance[5] === row.symbol}">
            {{ formatChange(row['5Y']) }}
          </td>

        </tr>
      </template>
    </DataGridTable>

    <DataGridTable :is-compact="true"
                   key="sectorHoldings"
                   :data="holdingsList"
                   :hovered-columns="true"
                   v-if="holdingsToDisplay"
                   :height-decrease="52"
                   :default-sort="'tradingData.changeP'"
                   :heightElements="['tableToolbar']"
                   :excludeHeaders="['sector','industry']"
                   :extra-headers="[{
                     position: 2,
        label: this.dictionary.sectors.sectorRatio,
        sortValue: 'weight'
      }]"
                   :default-sort-direction="defaultHoldingsSortDirection"
                   :fixed-col="0">
      <template
          #thead="{thClick, sortKey, sortDirection, toggleSymbolActions, showSymbolActions, headers}">
        <HeadCell v-for="(th, thIndex) in headers"
                  :sortKey="sortKey"
                  :header="th"
                  :key="`th-${thIndex}`"
                  :sortDirection="sortDirection"
                  :sortValue="th.sortValue"
                  :showSymbolActions="showSymbolActions"
                  @toggleSymbolActions="toggleSymbolActions()"
                  @sort="thClick(th.sortValue, thIndex)">
          {{ th.label }}
        </HeadCell>
      </template>
      <template #tbody="{rows, showSymbolActions}">
        <tr v-for="(row, rowIndex) in rows" :key="`rh-${rowIndex}`">
          <td style="cursor: pointer">

              <symbol-tag :tags="row.tags" :options="{link: true, chart: true, logo: 'small'}"
                          :showSymbolActions="showSymbolActions" :symbol="row.symbol"/>

          </td>


          <td>
            <symbol-label :key="`s-${row.symbol}`"
                          v-if="row.tradingData"
                          :pre-market-quote="row.tradingData?.preMarket"
                          :post-market-quote="row.tradingData?.postMarket"
                          :onlyPostMarket="activeButton === 'postMarket'"
                          :onlyPreMarket="activeButton === 'preMarket'"
                          :symbol="row.tradingData" :price-only="true"
                          :force-pre-post="activeButton !== 'tradingHours'"
                          :hide-pre-post="activeButton === 'tradingHours'"
                          :show-prePost="activeButton !== 'tradingHours'" :always-on="true"/>
            <span v-else>-</span>
          </td>
          <td>
            {{ row.weight }}
          </td>
   <VolumeCell :volume="row.tradingData?.formattedVolume" :trend="row.tradingData?.volumeTrend" />
          <td>
            {{ row.tradingData?.formattedMarketCap ?? '-' }}
          </td>
             <NumericCell
              :value="row.tradingData?.atr?.atrPercent"
              :ranges="[0, 1, 3, 5, 7]"
              :classes="['low','moderate','medium','high','extreme']"
          />
          <TrendIconCell :trend="row.tradingData?.atr?.atrTrend"/>
          <TrendIconCell :trend="row.tradingData?.obvTrend"/>
          <NumericCell v-for="period in ['5D','10D','1M']" :key="`n-${rowIndex}-${period}`" :value="row.eods?.[period]"/>
          <PriceDistance :tag="'td'" :symbol="row.symbol" :threshold="3"
                         :previousClose="row.tradingData?.price" :direction="'top'"
                         :defaultPrice="row.tradingData?.price" :value="row.tradingData?.ma50"
                         @deltaChange="deltaChange(row.symbol, $event, 'ma50D')"/>

          <PriceDistance :tag="'td'" :symbol="row.symbol" :threshold="3"
                         :previousClose="row.tradingData?.price" :direction="'bottom'"
                         :defaultPrice="row.tradingData?.price" :value="row.tradingData?.ma200"
                         @deltaChange="deltaChange(row.symbol, $event, 'ma200D')"/>

          <YearlyRangeCell :delta-change="this.deltaChange" :row="row"/>
          <RSICell :rsi="row.tradingData?.rsi"/>
          <MACDCell :macd="row.tradingData?.macd"/>
       
          <NumericCell :noColor="true" :value="row.tradingData?.eps"/>
          <NumericCell :noColor="true" :value="row.tradingData?.pe"/>
        </tr>
      </template>
    </DataGridTable>

  </section>
</template>

<script>

import DailyBriefService from '../services/dailyBrief';
import SymbolLabel from "../components/symbols/symbol";
import SmallLoader from "../components/ui/SmallLoader";
import {useNewYorkTime} from "../stores/newYorkTime";

import useWatchlist from '../composables/addRemoveSymbolFromWatchlist';
import SortHoldings from "../components/sectors/SortHoldings";
import eventBus from "../services/events";
import Barchart from "../components/charts/BarChart";
import PriceChange from "./symbolView/PriceChange";
import OverlayUI from "../components/ui/Overlay";
import BigLoader from "../components/ui/BigLoader";
import SymbolLogo from "../components/symbols/symbolLogo";
import SymbolLink from "../components/symbols/symbolLink";
import quoteStreaming from "../composables/quoteStreaming";
import DataGrid from "../components/dataGrid/DataGrid";
import HeadCell from "../components/dataGrid/HeadCell";
import DataGridTable from "../components/dataGrid/DataGridTable";
  import SymbolChart from "../components/symbols/symbolChart";
import TableToolbar from "../components/ui/TableToolbar";
import PriceDistance from "../components/priceTags/priceDistance";
import UserService from "../services/user";
import SymbolTag from "../components/symbols/symbolTag";
import PriceRange from "../components/priceTags/priceRange";
import NumericCell from "../components/dataGrid/NumericCell";
import YearlyRangeCell from "../components/dataGrid/YearlyRangeCell";
import RSICell from "../components/dataGrid/RSICell";
import MACDCell from "../components/dataGrid/MACDCell";
import TrendIconCell from "../components/dataGrid/TrendIconCell";
import VolumeCell from "../components/dataGrid/VolumeCell";


export default {
  name: "SectorsView",
  components: {
    TrendIconCell,
    MACDCell,
    RSICell,
    YearlyRangeCell,
    NumericCell,
    PriceRange,
    SymbolTag,
    PriceDistance,
    TableToolbar,
    SymbolChart,
    DataGridTable,
    HeadCell,
    DataGrid,
    SymbolLink,
    SymbolLogo, BigLoader, OverlayUI, PriceChange, Barchart, SortHoldings, SmallLoader, SymbolLabel,
    VolumeCell
  },
  data() {
    return {
      globalKey: 0,
      sectorsHeaders: [{
        label: this.dictionary.symbolPage.name,
        sortValue: 'name',
        allowActionsHiding: true
      },
        {
          label: this.dictionary.trendingStocks.priceChange + ' ' + this.dictionary.chart.periods['1d'],
          sortValue: '1D'
        },
        {
          label: this.dictionary.trendingStocks.priceChange + ' ' + this.dictionary.chart.periods['5d'],
          sortValue: '5D'
        },
        {
          label: this.dictionary.trendingStocks.priceChange + ' ' + this.dictionary.chart.periods['10d'],
          sortValue: '10D'
        },
        {
          label: this.dictionary.trendingStocks.priceChange + ' ' + this.dictionary.chart.periods['1m'],
          sortValue: '1M'
        },
        {
          label: this.dictionary.trendingStocks.priceChange + ' ' + this.dictionary.chart.periods['6m'],
          sortValue: '6M'
        },
        {
          label: this.dictionary.trendingStocks.priceChange + ' ' + this.dictionary.chart.periods['ytd'],
          sortValue: 'ytd'
        },
        {
          label: this.dictionary.trendingStocks.priceChange + ' ' + this.dictionary.chart.periods['3y'],
          sortValue: '3Y'
        },
        {
          label: this.dictionary.trendingStocks.priceChange + ' ' + this.dictionary.chart.periods['5y'],
          sortValue: '5Y'
        },
      ],

      holdingsToDisplay: null,
      defaultSort: 'symbol',
      defaultSortDirection: -1,
      defaultHoldingsSortDirection: -1,
      defaultHoldingsSort: 'changeP',
      xAxisScrolled: false,
      isLoading: false,
      sectors: [],
      watchlist: useWatchlist(),
      holdingsList: [],
      refreshInterval: null,
      showHoldingsForSymbol: null,
      holdingsLoading: {},
      currentlyLoading: null,
      activeTab: null,
    }
  },
  setup() {
    const nyTime = useNewYorkTime();
    const {updateStream, leaveStream} = quoteStreaming();
    const {addSymbolToWatchlist, removeSymbolFromWatchlist, watchlist} = useWatchlist();

    return {nyTime, addSymbolToWatchlist, removeSymbolFromWatchlist, watchlist, updateStream, leaveStream}
  },
  computed: {
    bestPerformance() {

      if (!this.sectors.length === 0) return {}

      let performance = {};
      let worsePerformance = {};

      let keys = ['1D', '5D', '10D', '1M', '6M', 'ytd', '3Y', '5Y']

      for (let i = 0; i < keys.length; i++) {
        let bestPerformance = null;
        let worsePerformanceP = null;
        this.sectors.forEach(symbol => {

          if (symbol[keys[i]]) {
            const change = symbol[keys[i]];
            if (change > bestPerformance || bestPerformance === null) {
              bestPerformance = change;
              performance[i] = symbol.symbol;
            }

            if (change < worsePerformanceP || worsePerformanceP === null) {
              worsePerformanceP = change;
              worsePerformance[i] = symbol.symbol;
            }
          }
        });
      }

      return {performance, worsePerformance};
    },
  },
  methods: {
    deltaChange(symbol, value, key) {

      this.holdingsList = this.holdingsList.map(row => {
        if (row.symbol === symbol) {
          row[key] = value;
        }
        return row;
      });
    },
    rowClick(symbol) {

      this.holdingsToDisplay = symbol;
      this.showHoldings();
    },
    showSectorNews(symbol) {
      eventBus.emit('navigate', {type: 'feed', value: 'sector', symbol: symbol})
    },
    formatChange(value) {
      return value ? value + '%' : '-'
    },
    scrolled(ev) {

      const scrollLeftPosition = ev.target.scrollLeft;
      this.xAxisScrolled = Math.abs(scrollLeftPosition) > 10;
    },
    async addRemoveWatchlistSymbol(symbol) {

      if (this.watchlist.watchlist.includes(symbol)) {

        try {
          mixpanel.track('symbol:remove', {client: window.location.host, symbol: symbol});
        } catch (e) {
        }

        const watchlist = await UserService.removeSymbolFromWatchlist(symbol)
        this.watchlist.updateWatchlist(watchlist);
      } else {

        try {
          mixpanel.track('symbol:add', {client: window.location.host, symbol: symbol});
        } catch (e) {
        }
        const watchlist = await UserService.addSymbolToWatchlist(symbol)
        this.watchlist.updateWatchlist(watchlist);
      }
    },
    back() {

      this.holdingsToDisplay = null;
      this.holdingsList = [];
      this.leaveStream();
      this.globalKey++;
    },
    async getSectors(skipReload = false) {


      if (!skipReload) this.isLoading = true;
      this.sectors = await DailyBriefService.sectorsPerformance();
      if (!skipReload) this.isLoading = false;
    },
    async loadHoldings(symbol) {
      this.holdings[symbol] = await DailyBriefService.getSectorsHoldings(symbol);
    },
    async showHoldings() {

      this.holdingsList = await DailyBriefService.getSectorsHoldings(this.holdingsToDisplay.symbol);
      this.updateStream(this.holdingsList.map(holding => holding.symbol), `sector-${this.holdingsToDisplay.symbol}`);
      this.globalKey++;

    }
  },
  beforeRouteLeave() {

    clearInterval(this.refreshInterval);
  },
  beforeUnmount() {
    clearInterval(this.refreshInterval);
  },
  async mounted() {
    await this.getSectors();

    if (!this.refreshInterval) {
      this.refreshInterval = setInterval(async () => {

        if (this.nyTime.isMarketOpen) {
          await this.getSectors(true);

          for (let k in this.holdings) {

            await this.getSectorsHoldings(k);
          }
        }
      }, 60000);
    }
  }
}
</script>

<style scoped lang="scss">

@import "../assets/style/vars";

main {

  h3 {
    margin: 0;
    padding: 5px $minSpacing;
  }
}

.holdings {
  //overflow-y: hidden;
  height: 0;
  transition: $toggleTransition;
  white-space: nowrap;

}

#barchartWrapper {
  margin: 0 0 -5rem;
  @media (max-width: 660px) {
    overflow-x: scroll;
  }
}

.sector {
  position: absolute;
  bottom: 0;
  height: 90%;
  left: 5%;
  width: 90%;
  background: var(--bgColor);
  z-index: 9999;
  border-radius: 12px;
  overflow: auto;
  box-shadow: 0 5px 20px 0 rgba(0, 0, 0, 0.2);
  border-bottom: 1px solid var(--notficationBorderColor);

  @media(min-width: 1460px) {
    width: 70%;
    left: 15%;
  }

  @media(max-width: 660px) {
    height: 95%;
    width: 98%;
    left: 1%;
  }

  .sectorRow {
    padding: 5px 0;
    display: flex;
    align-items: center;

    &:hover {
      background: var(--tableHoverColor);
    }

    .holdingsToolbar[dir="rtl"] {
      margin: 0 auto 0 0;
    }

    .holdingsToolbar {
      margin: 0 0 0 auto;
    }
  }

  a {
    position: absolute;
    left: 0;
    top: 0;
    width: 100%;
    height: 100%;
  }
}


.symbolName {
  font-weight: 600;
  font-size: 18px;
  padding: 0 1rem;
  display: flex;
  align-items: center;
  gap: 10px;
}

.companyName {
  padding: 0 1rem;
  unicode-bidi: plaintext;
  @media(max-width: 660px) {
    padding: .5rem 1rem 0;
  }
}

.companyName[dir="rtl"] {
  text-align: right;
}


.sectorSymbol {
  justify-content: space-between;
  margin: 0 1rem !important;
  border-bottom: 1px solid var(--notficationBorderColor);

  @media(max-width: 600px) {
    gap: 5px;
    flex-direction: column;
    justify-content: flex-start !important;
    align-items: flex-start !important;
  }

  .symbolName {
    font-size: 16px;
    @media(max-width: 600px) {
      padding: .5rem 0 0 0;
    }
  }

  .companyName {
    @media(max-width: 600px) {
      padding: 0;
      font-size: 14px;
    }
  }
}

.sectorHeader {
  position: sticky;
  top: 0;
  background: var(--bgColor);
  border-bottom: 1px solid var(--notficationBorderColor);
  z-index: 1;
}

.symbolInfo {
  display: flex;
  align-items: center;
  gap: 20px;

  @media(max-width: 660px) {
    gap: 0;
  }

  .holdingStat {
    width: 80px;
    unicode-bidi: plaintext;

    &.filter {
      user-select: none;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 3px;
      font-size: 13px;
      font-weight: 600;
      height: 30px;
    }

    label {
      opacity: .6;
      font-weight: 500;
      font-size: 13px;
      display: none;
      @media(max-width: 600px) {
        display: block;
      }
    }
  }

  .holdingStat[dir="rtl"] {
    text-align: right;
    direction: rtl;
  }
}

.toggleHoldings {
  cursor: pointer;
  padding: 10px;
  font-size: 20px;
  border-radius: 5px;
  margin: 0 10px;
  //
  //@media (max-width: 600px) {
  //  display: none;
  //}

  @media(min-width: 600px) {
    display: block;
  }

  &:hover {
    background: var(--highlightColorOpacity);
    color: var(--highlightColor)
  }
}

.toggleHoldings.mobile {
  @media (max-width: 600px) {
    display: block;
  }
  @media(min-width: 600px) {
    display: none;
  }

  cursor: pointer;
  position: absolute;
  left: 0;
  top: 25%;
  transform: translateY(-50%);
}

.holdingsToolbar {
  margin: 0 1.5rem 0 2rem;
}

.sectorTools {
  display: flex;
  align-items: center;
  gap: 15px;
  //margin: 0 0 0 auto;
}

.sectorTools[dir="rtl"] {
  //margin: 0 auto 0 0;
}

.sectorOpen {
  background: var(--bgColor);
  backdrop-filter: blur(20px);
  padding: .35rem 1rem;
  border-bottom: 1px solid #eee;
  box-shadow: 0 5px 5px 0 rgba(15, 15, 15, .05);
  position: sticky;
  left: 0;
  z-index: 2;
  font-size: 22px;

  @media(max-width: 600px) {
    //flex-direction: column;
    padding: 15px 0;
    align-items: flex-start !important;
  }
}

#priceChangeTable {
  //padding: $minSpacing;
}


.companyHoldingName {

  @media(max-width: 660px) {
    max-width: 130px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    display: block;
  }
}

.stickySymbol {
  background: var(--bgColor);
  position: sticky;
  left: 0;
  border-right: 1px solid var(--tableBorderColor);
  z-index: 1;

  &[dir="rtl"] {
    border-right: unset;
    border-left: 1px solid var(--tableBorderColor);
    left: unset;
    right: 0;
  }
}

.xAxisScrolled {
  box-shadow: 3px 0 2px 0 rgba(0, 0, 0, 0.05);
  border-right: 1px solid var(--notficationBorderColor);

  &[dir="rtl"] {
    border-right: unset;
    border-left: 1px solid var(--notficationBorderColor);
    box-shadow: -3px 0 2px 0 rgba(0, 0, 0, 0.05);
  }
}


.bestPerformer {
  color: var(--green);
  background: var(--opacityGreen) !important;
  font-weight: 600;
}

.worsePerformer {
  color: var(--red);
  font-weight: 600;
  background: var(--opacityRed) !important;
}

#back {
  display: flex;
  align-items: center;
  gap: $minSpacing;
  margin-top: -2px;
  padding-bottom: 5px;
  color: var(--highlightColor);
  cursor: pointer;
  font-weight: 600;
  font-size: 20px;
  letter-spacing: .6px;

  i {
    margin-top: 2px;
  }
}

.addRemoveSymbol {
  flex: 0 0 55px !important;
  width: 45px;
  margin-left: auto;

  &[dir="rtl"] {
    margin-left: unset;
    margin-right: auto;
  }

  i {
    //display: none;
  }

  @media(max-width: 660px) {

    flex: 0 0 30px !important;
    width: 30px;
    height: 20px;
    box-sizing: border-box;
    justify-content: center;
    align-items: center;
    i {
      display: block;
    }

    span {
      //display: none;
    }
  }
}

</style>
